import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocationsService } from '../locations.service';
import {
  createLocationSurveyPoint,
  createLocationSurveyPointFailure,
  createLocationSurveyPointSuccess,
  loadLocationById,
  loadLocationByIdFailure,
  loadLocationByIdSuccess,
  loadLocations,
  loadLocationsFailure,
  loadLocationsSuccess,
  loadLocationSurveyPoints,
  loadLocationSurveyPointsFailure,
  loadLocationSurveyPointsSuccess,
  searchSurveyPoints,
  searchSurveyPointsFailure,
  searchSurveyPointsSuccess,
  setLocationSurveyPointsInactive,
  setLocationSurveyPointsInactiveSuccess,
  updateLocationCountries,
} from './locations.actions';

@Injectable()
export class LocationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly locationsService = inject(LocationsService);

  public loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLocations),
      switchMap(() =>
        this.locationsService.getLocations().pipe(
          map((locations) =>
            loadLocationsSuccess({
              locations,
              loading: false,
              searchedSurveyPoints: [],
              buttonLoading: false,
            })
          ),
          catchError((error) =>
            of(loadLocationsFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public loadSurveyPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLocationSurveyPoints),
      switchMap((action) =>
        this.locationsService.getLocationSurveyPoints(action.id).pipe(
          map((surveyPoints) =>
            loadLocationSurveyPointsSuccess({ surveyPoints, loading: false })
          ),
          catchError((error) =>
            of(loadLocationSurveyPointsFailure({ error, loading: false }))
          )
        )
      )
    )
  );
  public updateLocationCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateLocationCountries),
      switchMap((action) =>
        this.locationsService
          .updateLocationCountries(action.locationId, action.countries)
          .pipe(
            map((location) =>
              loadLocationByIdSuccess({ location, loading: false })
            )
          )
      )
    )
  );
  public getLocationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLocationById),
      switchMap(({ id }) =>
        this.locationsService.getLocationById(id).pipe(
          map((location) =>
            loadLocationByIdSuccess({ location, loading: false })
          ),
          catchError((error) =>
            of(loadLocationByIdFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public createLocationSurveyPoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createLocationSurveyPoint),
      switchMap((action) =>
        this.locationsService
          .createLocationSurveyPoint(action.locationId, action.surveyPoint)
          .pipe(
            map((surveyPoint) =>
              createLocationSurveyPointSuccess({ surveyPoint, loading: false })
            ),
            catchError((error) =>
              of(createLocationSurveyPointFailure({ error, loading: false }))
            )
          )
      )
    )
  );

  public setSurveyPointsInactive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLocationSurveyPointsInactive),
      switchMap(({ surveyPoints, locationId }) => {
        const updateObservables = surveyPoints.map((surveyPoint) =>
          this.locationsService
            .getSurveyPointById(surveyPoint.id as number)
            .pipe(
              switchMap((actualSurveyPoint) =>
                this.locationsService.updateSurveyPoint(
                  locationId,
                  actualSurveyPoint.id,
                  {
                    ...actualSurveyPoint,
                    active: false,
                  }
                )
              )
            )
        );
        if (updateObservables.length === 0) {
          return of(setLocationSurveyPointsInactiveSuccess());
        }
        return forkJoin(updateObservables).pipe(
          map(() => setLocationSurveyPointsInactiveSuccess()),
          catchError(() => of(setLocationSurveyPointsInactiveSuccess()))
        );
      })
    )
  );

  public searchSurveyPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchSurveyPoints),
      switchMap(({ searchObj }) =>
        this.locationsService.searchSurveyPoints(searchObj).pipe(
          map((searchedSurveyPoints) =>
            searchSurveyPointsSuccess({ searchedSurveyPoints, loading: false })
          ),
          catchError((error) =>
            of(searchSurveyPointsFailure({ error, loading: false }))
          )
        )
      )
    )
  );
}
